import React, { useEffect, useState } from "react";
import { Box, Card, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import { sectionTitleSpacingSx } from "../sectionHeaderStyles";

const PROMOTIONS_API_URL = "https://uktomars.co.uk/api/promotions";
const STORAGE_BASE_URL = "https://api.uktomars.co.uk/storage/";
const PROMOTION_IMAGE_ASPECT_RATIO = "1200 / 1800";
const PAGE_MAX_WIDTH = 1480;

const pageContentSx = {
  maxWidth: PAGE_MAX_WIDTH,
  mx: "auto",
  px: { xs: 2.5, sm: 3, md: 4 },
  width: "100%",
  boxSizing: "border-box",
};

const pageBrandSx = {
  navy: NAV.navy,
  blue: "#1e5a9e",
  muted: "#5c6370",
};

const headerFont = {
  label: { xs: "0.8rem", sm: "0.875rem", md: "0.95rem" },
  title: { xs: "1.65rem", sm: "1.9rem", md: "2.25rem", lg: "2.5rem" },
  subtitle: { xs: "1rem", sm: "1.1rem", md: "1.2rem", lg: "1.25rem" },
};

const toStorageUrl = (value = "") => {
  if (!value) return "";
  if (value.startsWith("http")) return value;
  return `${STORAGE_BASE_URL}${value.replace(/^\/+/, "")}`;
};

const PromotionsPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const loadPromotions = async () => {
      try {
        setLoading(true);
        const response = await fetch(PROMOTIONS_API_URL);
        if (!response.ok) throw new Error("Failed to fetch promotions");
        const payload = await response.json();
        const data = Array.isArray(payload?.data) ? payload.data : [];

        if (isMounted) {
          setOffers(data);
          setError("");
        }
      } catch {
        if (isMounted) {
          setOffers([]);
          setError("Unable to load promotions right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPromotions();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        bgcolor: "#fff",
        pt: "clamp(96px, 12vh, 140px)",
        pb: { xs: 5, md: 7 },
      }}
    >
      <Box sx={pageContentSx}>
        <Box
          component="header"
          sx={{ mb: { xs: 3, md: 4 }, textAlign: "center" }}
        >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            color: pageBrandSx.navy,
            fontSize: headerFont.title,
            lineHeight: { xs: 1.25, md: 1.2 },
            ...sectionTitleSpacingSx,
            mb: { xs: 1.5, md: 2 },
            px: { xs: 1, sm: 0 },
          }}
        >
          Latest FMCG Offers &amp; Wholesale Opportunities
        </Typography>

        <Typography
          variant="body1"
          component="p"
          sx={{
            color: pageBrandSx.muted,
            maxWidth: 820,
            mx: "auto",
            fontSize: headerFont.subtitle,
            lineHeight: { xs: 1.65, md: 1.7 },
            fontWeight: 400,
            px: { xs: 1, sm: 0 },
            mb: 1.5,
          }}
        >
          Explore featured products, new arrivals, seasonal promotions, and
          competitive wholesale deals across British and European FMCG brands.
          From mixed pallets to container quantities, we help businesses source
          quality products at competitive trade prices.
        </Typography>

        <Typography sx={{ fontSize: 14, color: pageBrandSx.muted }}>
          {loading ? "Loading offers..." : `${offers.length} offer(s) found`}
        </Typography>

        <Box
          sx={{
            width: 44,
            height: 4,
            bgcolor: pageBrandSx.blue,
            borderRadius: 1,
            mx: "auto",
            mt: 2.5,
          }}
        />
        </Box>

        {error ? (
          <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
            {error}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: { xs: 3, md: 4 },
          }}
        >
          {offers.map((offer) => (
          <Card
            key={offer.id ?? offer.title}
            elevation={0}
            component="a"
            href={
              toStorageUrl(offer?.pdf_file_url || offer?.pdf_url) || undefined
            }
            target="_blank"
            rel="noreferrer"
            sx={{
              border: `2px solid ${pageBrandSx.blue}`,
              borderRadius: 2.5,
              overflow: "hidden",
              textDecoration: "none",
              backgroundColor: "#ffffff",
              p: 0.75,
              cursor: toStorageUrl(offer?.pdf_file_url || offer?.pdf_url)
                ? "pointer"
                : "default",
              boxShadow: "0 4px 16px rgba(15, 40, 71, 0.08)",
              transition: "transform 0.22s ease, box-shadow 0.22s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 28px rgba(15, 40, 71, 0.12)",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                borderRadius: 1.5,
                overflow: "hidden",
                aspectRatio: PROMOTION_IMAGE_ASPECT_RATIO,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#ffffff",
              }}
            >
              {toStorageUrl(offer?.image_url || offer?.image) ? (
                <img
                  src={toStorageUrl(offer?.image_url || offer?.image)}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              ) : (
                <Typography sx={{ fontSize: 13, color: pageBrandSx.muted }}>
                  No image
                </Typography>
              )}
            </Box>
          </Card>
          ))}
        </Box>

        {!loading && !error && offers.length === 0 ? (
          <Typography sx={{ mt: 2, color: pageBrandSx.muted, textAlign: "center" }}>
            No offers available right now.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default PromotionsPage;
